import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectShowStateModal } from '@brands/store/selectors/location';
import { setShowStateModal } from '@brands/store/slices/location';
import { useEffect, useRef } from 'react';

/**
 * Opens the location (StateModal) prompt on a page that needs the patient's
 * current state before it can query anything useful.
 *
 * Normally `usePostAuth` raises `showStateModal` at sign-in and the dashboard
 * or SelectPatient renders the modal. A reschedule started from an email link
 * skips both of those pages: the patient logs in and lands straight on
 * select-provider / select-care-advisor, so nothing ever renders the modal and
 * the availability request goes out with `license_states=` — which matches no
 * provider, leaving the patient on an empty list with nothing to act on.
 *
 * The prompt is raised once per mount. A patient who dismisses it stays
 * dismissed (the effect won't reopen it on the next render), and the page is
 * responsible for reflecting a state that arrives later — saving the modal
 * writes it to `currentPatientState`, which the caller's license filter reads.
 */
export const usePatientLocationStatePrompt = (
  needsLocationState: boolean,
): { isStateModalOpen: boolean; closeStateModal: () => void } => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const showStateModal = useAppSelector(selectShowStateModal);
  const hasPrompted = useRef(false);

  // StateModal writes the state against the signed-in user, so it only makes
  // sense for patients scheduling for themselves — for staff the licensing
  // state comes off the patient's own record, not whoever is driving.
  const isSelfSchedulingRole =
    userInfo?.role?.name === UserRoleName.Patient || userInfo?.role?.name === UserRoleName.Dependent;

  useEffect(() => {
    if (!needsLocationState || !isSelfSchedulingRole || hasPrompted.current) return;
    hasPrompted.current = true;
    dispatch(setShowStateModal({ showStateModal: true }));
  }, [needsLocationState, isSelfSchedulingRole, dispatch]);

  const closeStateModal = (): void => {
    dispatch(setShowStateModal({ showStateModal: false }));
  };

  return { isStateModalOpen: showStateModal && isSelfSchedulingRole, closeStateModal };
};

export default usePatientLocationStatePrompt;
